import { useMemo } from 'react';
import { HostingEnvironment } from '@opengov/psp-contract';
import {
  NavigationConfig,
  MenuSection,
  MenuItem,
  AppConfig,
  AppCategory,
  MenuSectionTitle,
  EntityApplication,
  EntityApplicationProductKey,
  EntitlementProductKey,
  MenuGroupName,
} from '../types';
import { isGabDynamicEntitlementKey } from './gabDynamicAppConfigs';
import { IconName, getMenuGroupIconName } from '../utils/icons';
import {
  detectPspHostingEnvironment,
  isRemoteApp,
  shouldShowSecretPspDevTools,
  shouldShowDataCreatorInIntegration,
} from './utils';
import { useEntity } from '../contexts/EntityContext';
import { usePspContext } from '@/contexts/PspContext';
import { useFeatureFlag, FeatureFlagKey } from '../hooks/useFeatureFlags';
import { usePinnedProducts } from '../hooks/usePinnedProducts';

/**
 * Navigation Configuration
 *
 * This file dynamically generates the navigation sidebar structure from the app registry.
 * Menu sections and items are automatically created based on:
 * - Static apps: Always shown (must have a component, not moduleRoutes)
 * - Test apps: Only shown in development mode (can have moduleRoutes or component)
 * - Entitled apps: Filtered based on user entitlements (can have moduleRoutes or component)
 *
 * ## Developer Override
 * To show all entitled apps regardless of API entitlements (useful for development/testing):
 * - Use the toggle in the App Registry Editor page, OR
 * - Set via sessionStorage:
 * ```javascript
 * sessionStorage.setItem('psp.showAllEntitledApps', 'true');  // Enable
 * sessionStorage.setItem('psp.showAllEntitledApps', 'false'); // Disable
 * sessionStorage.removeItem('psp.showAllEntitledApps');       // Disable (alternative)
 * ```
 * Changes are stored in sessionStorage (cleared when browser tab closes) and will take effect after a page refresh. Works in all environments.
 *
 * ## Component Association
 * Components are defined directly in the app registry (appRegistry.ts).
 * - Local apps (static apps and some test/entitled apps) have a `component` property
 * - Remote apps (test/entitled apps with moduleRoutes) have a `moduleRoutes` property with route patterns and resolvers
 * - Apps cannot have both moduleRoutes and component
 *
 * To add a component to an app, update the app entry in appRegistry.ts:
 * ```typescript
 * import { MyPage } from '../pages/MyPage';
 *
 * {
 *   id: 'my-app',
 *   component: MyPage, // For local apps
 *   // OR
 *   moduleRoutes: [
 *     { pattern: '/', resolver: (ctx) => 'https://...' }, // For remote apps
 *   ],
 *   // ... other fields
 * }
 * ```
 */

/**
 * Badge state map type.
 * Maps app IDs to badge visibility (true = show badge, false = hide badge).
 * Can be extended in the future to support count badges and colors.
 */
export type BadgeStateMap = Record<string, boolean>;

// ============================================================================
// DEVELOPER OVERRIDE: Show All Entitled Apps
// ============================================================================
// TODO: Remove this entire section when this dev feature is no longer desired
// This allows developers to see all entitled apps in the sidebar regardless of API entitlements
// To remove: Delete this section and remove the showAllEntitled check in filterAppsForNavigation

const SHOW_ALL_ENTITLED_APPS_KEY = 'psp.showAllEntitledApps';

/**
 * Developer override: Check if all entitled apps should be shown regardless of API entitlements.
 * Uses sessionStorage (cleared when browser tab closes) to prevent accidental persistence.
 * Enable by setting sessionStorage.setItem('psp.showAllEntitledApps', 'true')
 * Disable by setting sessionStorage.setItem('psp.showAllEntitledApps', 'false') or removing the key
 *
 * @returns true if override is enabled, false otherwise
 */
function isShowAllEntitledAppsEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const value = sessionStorage.getItem(SHOW_ALL_ENTITLED_APPS_KEY);
    return value === 'true';
  } catch (error) {
    console.warn('[navigationConfig] Failed to read showAllEntitledApps override:', error);
    return false;
  }
}

/**
 * Sets the developer override to show all entitled apps.
 * Uses sessionStorage (cleared when browser tab closes) to prevent accidental persistence.
 * Exported for use in UI components (e.g., AppRegistryEditorPage).
 *
 * @param enabled - true to enable override, false to disable
 */
export function setShowAllEntitledApps(enabled: boolean): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (enabled) {
      sessionStorage.setItem(SHOW_ALL_ENTITLED_APPS_KEY, 'true');
    } else {
      sessionStorage.removeItem(SHOW_ALL_ENTITLED_APPS_KEY);
    }
  } catch (error) {
    console.warn('[navigationConfig] Failed to set showAllEntitledApps override:', error);
  }
}

/**
 * Gets the current state of the developer override.
 * Exported for use in UI components (e.g., AppRegistryEditorPage).
 *
 * @returns true if override is enabled, false otherwise
 */
export function getShowAllEntitledApps(): boolean {
  return isShowAllEntitledAppsEnabled();
}

// ============================================================================

/**
 * Converts an AppConfig to a MenuItem.
 * Components are taken directly from the AppConfig (either from component or moduleRoutes).
 *
 * @param app - The app configuration
 * @param badgeState - Optional map of app IDs to badge visibility
 * @returns MenuItem for the navigation
 */
function appConfigToMenuItem(app: AppConfig, badgeState?: BadgeStateMap): MenuItem {
  const menuItem: MenuItem = {
    id: app.id,
    label: app.displayName,
    icon: app.icon,
    path: app.basePath,
    externalUrl: 'externalUrl' in app && app.externalUrl ? app.externalUrl : undefined,
    // Mark as remote app if it has a moduleRoutes property
    isRemoteApp: isRemoteApp(app),
    openInNewTab: 'openInNewTab' in app && app.openInNewTab === true && 'externalUrl' in app,
  };

  // Add component if it's a local app (has component, not moduleRoutes)
  if ('component' in app && app.component) {
    menuItem.component = app.component;
  }

  // Add dot badge if configured in badge state map
  if (badgeState && badgeState[app.id]) {
    menuItem.dotBadge = badgeState[app.id];
  }

  return menuItem;
}

/**
 * True when at least one claim key starts with one of the prefixes
 * (case-insensitive, matching how bedrock compares claim keys).
 */
export function hasClaimWithPrefix(
  claimKeys: ReadonlySet<string>,
  prefixes: readonly string[]
): boolean {
  const upperPrefixes = prefixes.map((prefix) => prefix.toUpperCase());
  for (const key of claimKeys) {
    const upperKey = key.toUpperCase();
    if (
      upperPrefixes.some((prefix) => upperKey.length > prefix.length && upperKey.startsWith(prefix))
    ) {
      return true;
    }
  }
  return false;
}

const STATIC_PRODUCT_KEYS: ReadonlySet<string> = new Set(
  Object.values(EntityApplicationProductKey)
);

/** Whether an app id can name an entitlement: a static product key, or a gab-core app key. */
const isEntitlementProductKey = (id: string): id is EntitlementProductKey =>
  STATIC_PRODUCT_KEYS.has(id) || isGabDynamicEntitlementKey(id);

/**
 * Filters apps based on category, entitlements and claims.
 * Supports developer override to show all entitled apps regardless of API entitlements.
 *
 * @param apps - All apps from the registry
 * @param entitlements - User entitlements from EntityContext
 * @param claimKeys - User's PLATFORM claim keys at the active entity from EntityContext.
 *   Apps with `requiredClaimPrefixes` are hidden when no key matches (empty = fail closed).
 * @returns Filtered apps that should be shown
 */
export function filterAppsForNavigation(
  apps: readonly AppConfig[],
  entitlements: EntityApplication[],
  claimKeys: ReadonlySet<string> = new Set()
): AppConfig[] {
  const entitledProductKeys = new Set<EntitlementProductKey>(
    entitlements.map((ent) => ent.entitlementProductKey)
  );

  // TODO: Remove this check when developer override feature is no longer desired
  const showAllEntitled = isShowAllEntitledAppsEnabled();

  // Detect hosting environment for Developer Tools visibility
  const hostingEnvironment = detectPspHostingEnvironment();
  const isDeveloperEnvironment =
    hostingEnvironment === HostingEnvironment.DEVELOPMENT ||
    hostingEnvironment === HostingEnvironment.INTEGRATION;

  const entitledAndFlaggedApps = apps.filter((app) => {
    // Developer Tools should only be visible in development and integration environments
    if (app.menuSection === MenuSectionTitle.DEVELOPER_TOOLS) {
      if (!isDeveloperEnvironment) {
        // Staging/production: only visible via the secret sessionStorage override
        return shouldShowSecretPspDevTools();
      }
      // In integration, the Data Creator is hidden by default.
      // To enable: localStorage.setItem('psp.showDataCreatorInIntegration', 'true')
      // or call window.toggleDataCreatorVisibility() in the browser console, then refresh.
      if (hostingEnvironment === HostingEnvironment.INTEGRATION && app.id === 'dev-data-creator') {
        return shouldShowDataCreatorInIntegration();
      }
      return true;
    }

    // Claim-gated apps (any category): the user must hold a matching claim.
    // TODO: Remove the showAllEntitled bypass with the developer override feature.
    if (
      app.requiredClaimPrefixes?.length &&
      !showAllEntitled &&
      !hasClaimWithPrefix(claimKeys, app.requiredClaimPrefixes)
    ) {
      return false;
    }

    // For entitled apps, check if the app's ID matches an entitlement product key
    if (app.category === AppCategory.ENTITLED) {
      // TODO: Remove this override check when developer override feature is no longer desired
      if (showAllEntitled) {
        return true;
      }
      // Normal behavior: check if the app's ID matches an entitlement product key.
      if (!isEntitlementProductKey(app.id) || !entitledProductKeys.has(app.id)) {
        return false;
      }
      if ('requiredEntitlements' in app && app.requiredEntitlements) {
        return app.requiredEntitlements.every((key) => entitledProductKeys.has(key));
      }
      return true;
    }

    // Allow all non-entitled app categories (e.g., static and test apps) without additional filtering.
    // Note: Test apps are already conditionally omitted in appRegistry.ts based on the environment.
    return true;
  });

  // When Subsidiary Ledgers is visible it is the only Financials item shown,
  // except AR and CR which have their own independent entitlement product keys
  // and are not part of the legacy FMS portal.
  // Keyed off Subsidiary Ledgers surviving the entitlement filter rather than
  // reading its flag directly, so an entity that is flagged on but not entitled
  // to Financials keeps the rest of the group.
  // Nav-only by design: applying this to the shared appRegistry broke route
  // generation and resolveResourceToApp() for unflagged siblings (#449).
  const subsidiaryLedgersVisible = entitledAndFlaggedApps.some(
    (app) => app.featureFlagKey === FeatureFlagKey.PSP_UI_SUBSIDIARY_LEDGER
  );
  if (!subsidiaryLedgersVisible) {
    return entitledAndFlaggedApps;
  }

  return entitledAndFlaggedApps.filter(
    (app) =>
      !('group' in app && app.group === MenuGroupName.FINANCIALS) ||
      app.featureFlagKey === FeatureFlagKey.PSP_UI_SUBSIDIARY_LEDGER ||
      app.id === EntityApplicationProductKey.ACCOUNTS_RECEIVABLE ||
      app.id === EntityApplicationProductKey.CASH_RECEIPTS
  );
}

/**
 * Groups apps by menu section and converts them to MenuItems.
 * Handles nested grouping for apps with a 'group' property.
 *
 * @param apps - Filtered apps to include in navigation
 * @param badgeState - Optional map of app IDs to badge visibility
 * @returns Array of MenuSection objects
 */
export function buildMenuSections(
  apps: AppConfig[],
  badgeState?: BadgeStateMap,
  pinnedPaths?: Set<string>,
  maxProductItems?: number
): MenuSection[] {
  // Group apps by menuSection
  const appsBySection = new Map<MenuSectionTitle, AppConfig[]>();

  apps.forEach((app) => {
    const section = app.menuSection;
    if (!appsBySection.has(section)) {
      appsBySection.set(section, []);
    }
    appsBySection.get(section)!.push(app);
  });

  // Convert to MenuSection array
  const sections: MenuSection[] = [];

  // Section configuration: defines order and collapsible properties
  const sectionConfig: Array<{
    title: MenuSectionTitle;
    collapsible: boolean;
    defaultExpanded: boolean;
  }> = [
    {
      title: MenuSectionTitle.TEST_APPS,
      collapsible: true,
      defaultExpanded: true,
    },
    {
      title: MenuSectionTitle.ACTION_HUBS,
      collapsible: false,
      defaultExpanded: false,
    },
    {
      title: MenuSectionTitle.PRODUCTS,
      collapsible: true,
      defaultExpanded: true,
    },
    {
      title: MenuSectionTitle.CAPABILITIES,
      collapsible: true,
      defaultExpanded: true,
    },
    {
      title: MenuSectionTitle.PREFERENCES,
      collapsible: false,
      defaultExpanded: false,
    },
    {
      title: MenuSectionTitle.DEVELOPER_TOOLS,
      collapsible: true,
      defaultExpanded: false,
    },
  ];

  sectionConfig.forEach((config) => {
    const sectionApps = appsBySection.get(config.title);
    if (!sectionApps || sectionApps.length === 0) {
      return;
    }

    const sectionId = config.title.toLowerCase().replace(/\s+/g, '-');

    // Separate grouped and non-grouped apps
    const groupedApps = new Map<string, AppConfig[]>();
    const nonGroupedApps: AppConfig[] = [];

    sectionApps.forEach((app) => {
      if ('group' in app && app.group) {
        if (!groupedApps.has(app.group)) {
          groupedApps.set(app.group, []);
        }
        groupedApps.get(app.group)!.push(app);
      } else {
        nonGroupedApps.push(app);
      }
    });

    // Build menu items
    const menuItems: MenuItem[] = [];

    // Add non-grouped items
    nonGroupedApps.forEach((app) => {
      menuItems.push(appConfigToMenuItem(app, badgeState));
    });

    // Add grouped items as parent/child structures
    groupedApps.forEach((apps, groupName) => {
      const children = apps.map((app) => appConfigToMenuItem(app, badgeState));

      // Sort children alphabetically by label
      children.sort((a, b) => a.label.localeCompare(b.label));

      const displayLabel = `${groupName}`;

      // Create parent menu item
      const groupId = `group-${displayLabel.toLowerCase().replace(/\s+/g, '-')}`;

      // Determine parent icon based on group name using utility function
      // Convert string groupName back to MenuGroupName enum
      const parentIcon: IconName = getMenuGroupIconName(groupName as MenuGroupName);

      const parentItem: MenuItem = {
        id: groupId,
        label: displayLabel,
        icon: parentIcon,
        path: `#${groupId}`, // Non-navigable path
        isGroup: true,
        children,
      };

      menuItems.push(parentItem);
    });

    const isProducts = config.title === MenuSectionTitle.PRODUCTS;

    // Sort: pinned products first (then alpha), all other sections alpha-only.
    menuItems.sort((a, b) => {
      if (isProducts && pinnedPaths) {
        const aPinned = pinnedPaths.has(a.path);
        const bPinned = pinnedPaths.has(b.path);
        if (aPinned !== bPinned) return aPinned ? -1 : 1;
      }
      return a.label.localeCompare(b.label);
    });

    // Stamp isPinned and apply the visible-item cap for the PRODUCTS section.
    let visibleItems = menuItems;
    if (isProducts && pinnedPaths) {
      visibleItems = menuItems.map((item) => ({ ...item, isPinned: pinnedPaths.has(item.path) }));
    }
    if (isProducts && maxProductItems != null) {
      visibleItems = visibleItems.slice(0, maxProductItems);
    }

    sections.push({
      id: sectionId,
      title: config.title,
      collapsible: config.collapsible,
      defaultExpanded: config.defaultExpanded,
      items: visibleItems,
    });
  });

  return sections;
}

const MAX_SIDEBAR_PRODUCTS = 7;

/**
 * Builds navigation configuration from app registry and entitlements.
 *
 * @param apps - All apps from the registry
 * @param entitlements - User entitlements from EntityContext
 * @param claimKeys - User's PLATFORM claim keys at the active entity from EntityContext
 * @param badgeState - Optional map of app IDs to badge visibility
 * @returns NavigationConfig object
 */
function buildNavigationConfig(
  apps: readonly AppConfig[],
  entitlements: EntityApplication[],
  claimKeys: ReadonlySet<string>,
  badgeState?: BadgeStateMap,
  flattenGroups?: boolean,
  pinnedPaths?: Set<string>
): NavigationConfig {
  const filteredApps = filterAppsForNavigation(apps, entitlements, claimKeys);

  // TODO: Remove this flattenGroups logic once the All Products feature ships to prod.
  // At that point, remove the `group` field and update CAPABILITIES entries to PRODUCTS
  // directly in the registry — no runtime remapping needed.
  const appsForMenu = flattenGroups
    ? filteredApps.map((app) => ({
        ...app,
        group: undefined,
        menuSection:
          app.menuSection === MenuSectionTitle.CAPABILITIES
            ? MenuSectionTitle.PRODUCTS
            : app.menuSection,
      }))
    : filteredApps;

  const sections = buildMenuSections(
    appsForMenu,
    badgeState,
    pinnedPaths,
    flattenGroups ? MAX_SIDEBAR_PRODUCTS : undefined
  );

  return {
    sections,
  };
}

/**
 * React hook that generates navigation configuration dynamically from app registry.
 * Filters apps based on user entitlements from EntityContext.
 *
 * @param badgeState - Optional map of app IDs to badge visibility
 * @returns NavigationConfig object
 */
export function useNavigationConfig(badgeState?: BadgeStateMap): NavigationConfig {
  const { activeEntityEntitledApplications, activeEntityClaimKeys } = useEntity();
  const pspContext = usePspContext();
  const isAllProductsEnabled = useFeatureFlag(FeatureFlagKey.PSP_UI_ALL_PRODUCTS_PAGE);
  const { pinnedPaths } = usePinnedProducts();
  const allApps = useMemo(() => pspContext?.appRegistry || [], [pspContext]);

  return useMemo(
    () =>
      buildNavigationConfig(
        allApps,
        activeEntityEntitledApplications,
        activeEntityClaimKeys,
        badgeState,
        isAllProductsEnabled,
        pinnedPaths
      ),
    [
      allApps,
      activeEntityEntitledApplications,
      activeEntityClaimKeys,
      badgeState,
      isAllProductsEnabled,
      pinnedPaths,
    ]
  );
}

/**
 * Legacy static export for backward compatibility.
 * @deprecated Use useNavigationConfig() hook instead.
 * This will be removed in a future version.
 */
export const navigationConfig: NavigationConfig = {
  sections: [],
};
